import { useTranslation } from 'react-i18next'

export function GuidePage() {
  const { t } = useTranslation()

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-10">
      <h1 className="text-3xl font-bold text-brand-700">{t('guide.title')}</h1>
      <p className="text-brand-600">{t('guide.intro')}</p>

      {/* Tärkeät linkit */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <p className="text-sm font-semibold text-brand-600 mb-2">{t('guide.links.title')}</p>
        <ul className="space-y-1 text-sm">
          <li>
            <span className="text-brand-700">{t('guide.links.register')}: </span>
            <a href="https://lataus.kilterinlinna.fi/register" className="text-brand-500 hover:underline break-all">
              https://lataus.kilterinlinna.fi/register
            </a>
          </li>
          <li>
            <span className="text-brand-700">{t('guide.links.login')}: </span>
            <a href="https://lataus.kilterinlinna.fi/" className="text-brand-500 hover:underline break-all">
              https://lataus.kilterinlinna.fi/
            </a>
          </li>
          <li>
            <span className="text-brand-700">{t('guide.links.guide')}: </span>
            <a href="https://lataus.kilterinlinna.fi/ohje" className="text-brand-500 hover:underline break-all">
              https://lataus.kilterinlinna.fi/ohje
            </a>
          </li>
          <li>
            <span className="text-brand-700">{t('guide.links.terms')}: </span>
            <a href="https://lataus.kilterinlinna.fi/lataus_kayttoehdot.pdf" target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline break-all">
              https://lataus.kilterinlinna.fi/lataus_kayttoehdot.pdf
            </a>
          </li>
        </ul>
      </div>

      {/* Sisällysluettelo */}
      <nav className="bg-white rounded-xl shadow-sm p-4">
        <p className="text-sm font-semibold text-brand-600 mb-2">{t('guide.toc')}</p>
        <ol className="list-decimal list-inside space-y-1 text-sm">
          <li><a href="#contract" className="text-brand-500 hover:underline">{t('guide.contract.title')}</a></li>
          <li><a href="#app" className="text-brand-500 hover:underline">{t('guide.app.title')}</a></li>
          <li><a href="#token" className="text-brand-500 hover:underline">{t('guide.token.title')}</a></li>
          <li><a href="#charging" className="text-brand-500 hover:underline">{t('guide.charging.title')}</a></li>
          <li><a href="#cable" className="text-brand-500 hover:underline">{t('guide.cable.title')}</a></li>
          <li><a href="#shared" className="text-brand-500 hover:underline">{t('guide.shared.title')}</a></li>
          <li><a href="#safety" className="text-brand-500 hover:underline">{t('guide.safety.title')}</a></li>
          <li><a href="#costs" className="text-brand-500 hover:underline">{t('guide.costs.title')}</a></li>
          <li><a href="#additional" className="text-brand-500 hover:underline">{t('guide.additional.title')}</a></li>
          <li><a href="#troubleshoot" className="text-brand-500 hover:underline">{t('guide.troubleshoot.title')}</a></li>
        </ol>
      </nav>

      {/* 1. Lataussopimus */}
      <Section number="1" id="contract" title={t('guide.contract.title')}>
        <ol className="list-decimal list-inside space-y-1 text-brand-700">
          <li>{t('guide.contract.step1')}</li>
          <li>{t('guide.contract.step2')}</li>
          <li>{t('guide.contract.step3')}</li>
          <li>{t('guide.contract.step4')}</li>
          <li>{t('guide.contract.step5')}</li>
          <li>{t('guide.contract.step6')}</li>
        </ol>
      </Section>

      {/* 2. Zaptec-sovellus */}
      <Section number="2" id="app" title={t('guide.app.title')}>
        <p className="text-brand-600 mb-3">{t('guide.app.intro')}</p>
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <a href="https://apps.apple.com/fi/app/zaptec/id1131162170?l=fi"
            target="_blank" rel="noopener noreferrer"
            className="text-brand-600 hover:underline font-medium">
            iPhone (App Store)
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.zaptec.zapapp"
            target="_blank" rel="noopener noreferrer"
            className="text-brand-600 hover:underline font-medium">
            Android (Google Play)
          </a>
        </div>
        <div className="flex gap-6 mt-4">
          <div className="text-center">
            <img src="/img/zaptec_appstore_qr.png" alt="App Store QR" className="w-32 h-32" />
            <p className="text-xs text-brand-400 mt-1">iPhone</p>
          </div>
          <div className="text-center">
            <img src="/img/zaptec_playstore_qr.png" alt="Google Play QR" className="w-32 h-32" />
            <p className="text-xs text-brand-400 mt-1">Android</p>
          </div>
        </div>
      </Section>

      {/* 3. Lataustunnisteen rekisteröinti */}
      <Section number="3" id="token" title={t('guide.token.title')}>
        <p className="text-brand-600 mb-2">{t('guide.token.intro')}</p>
        <ul className="list-disc list-inside text-brand-700 mb-3">
          <li>{t('guide.token.nfc')}</li>
          <li>{t('guide.token.abloy')}</li>
          <li>{t('guide.token.fob')}</li>
        </ul>
        <p className="text-brand-500 text-sm mb-3">{t('guide.token.multiple')}</p>

        <h4 className="font-semibold text-brand-700 mb-2">{t('guide.token.stepsTitle')}</h4>
        <ol className="list-decimal list-inside space-y-1 text-brand-700">
          <li>{t('guide.token.step1')}</li>
          <li>{t('guide.token.step2')}</li>
          <li>{t('guide.token.step3')}</li>
          <li>{t('guide.token.step4')}</li>
          <li>{t('guide.token.step5')}</li>
          <li>{t('guide.token.step6')}</li>
          <li>{t('guide.token.step7')}</li>
        </ol>

        <LoomEmbed id="bc82845dcdd94fa7b4d626c8f0ebbbd1" label={t('guide.token.video')} />
      </Section>

      {/* 4. Lataaminen */}
      <Section number="4" id="charging" title={t('guide.charging.title')}>
        <h4 className="font-semibold text-brand-700 mb-2">{t('guide.charging.withToken')}</h4>
        <ol className="list-decimal list-inside space-y-1 text-brand-700 mb-4">
          <li>{t('guide.charging.tokenStep1')}</li>
          <li>{t('guide.charging.tokenStep2')}</li>
          <li>{t('guide.charging.tokenStep3')}</li>
        </ol>

        <h4 className="font-semibold text-brand-700 mb-2">{t('guide.charging.withApp')}</h4>
        <ol className="list-decimal list-inside space-y-1 text-brand-700 mb-3">
          <li>{t('guide.charging.appStep1')}</li>
          <li>{t('guide.charging.appStep2')}</li>
        </ol>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
          <ImgCard src="/img/Home_view.PNG" alt={t('guide.charging.imgHome')} />
          <ImgCard src="/img/Choose_charger.PNG" alt={t('guide.charging.imgChoose')} />
          <ImgCard src="/img/Charging_confirmed.PNG" alt={t('guide.charging.imgConfirmed')} />
          <ImgCard src="/img/Charging_started.PNG" alt={t('guide.charging.imgStarted')} />
        </div>

        <ol className="list-decimal list-inside space-y-1 text-brand-700 mb-3" start={3}>
          <li>{t('guide.charging.appStep3')}</li>
          <li>{t('guide.charging.appStep4')}</li>
          <li>{t('guide.charging.appStep5')}</li>
        </ol>

        <LoomEmbed id="160a5daa82c34dc184510051c9c84f4d" label={t('guide.charging.video')} />

        <h4 className="font-semibold text-brand-700 mt-6 mb-2">{t('guide.charging.stopTitle')}</h4>
        <ol className="list-decimal list-inside space-y-1 text-brand-700">
          <li>{t('guide.charging.stopStep1')}</li>
          <li>{t('guide.charging.stopStep2')}</li>
        </ol>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg mt-3">
          {t('guide.charging.cableWarning')}
        </div>
      </Section>

      {/* 5. Latauskaapeli */}
      <Section number="5" id="cable" title={t('guide.cable.title')}>
        <p className="text-brand-600 mb-2">{t('guide.cable.intro')}</p>
        <ul className="list-disc list-inside text-brand-700 space-y-1">
          <li>{t('guide.cable.order')}</li>
          <li>{t('guide.cable.lock')}</li>
          <li>{t('guide.cable.ce')}</li>
        </ul>
      </Section>

      {/* 6. Yhteiskäyttö */}
      <Section number="6" id="shared" title={t('guide.shared.title')}>
        <p className="text-brand-600 mb-2">{t('guide.shared.intro')}</p>
        <p className="text-brand-700 mb-2">{t('guide.shared.cableReach')}</p>
        <p className="text-brand-700 mb-3">{t('guide.shared.swapSpace')}</p>
        <ul className="list-disc list-inside text-brand-700 space-y-1">
          <li>{t('guide.shared.disconnect')}</li>
          <li>{t('guide.shared.noIdle')}</li>
          <li>{t('guide.shared.dynamic')}</li>
        </ul>
      </Section>

      {/* 7. Turvallisuus */}
      <Section number="7" id="safety" title={t('guide.safety.title')}>
        <ul className="list-disc list-inside text-brand-700 space-y-1">
          <li>{t('guide.safety.inspect')}</li>
          <li>{t('guide.safety.damaged')}</li>
          <li>{t('guide.safety.tripping')}</li>
          <li>{t('guide.safety.vehicleFault')}</li>
          <li>{t('guide.safety.tamper')}</li>
        </ul>
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 rounded-lg mt-3">
          <strong>{t('guide.safety.emergencyTitle')}:</strong> {t('guide.safety.emergency')}
        </div>
      </Section>

      {/* 8. Kustannukset */}
      <Section number="8" id="costs" title={t('guide.costs.title')}>
        <div className="bg-brand-50 rounded-lg p-4 mb-3">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-brand-100">
                <td className="py-2 font-medium text-brand-700">{t('guide.costs.monthlyFee')}</td>
                <td className="py-2 text-brand-600">15,00 €/kk ({t('guide.costs.monthlyFeeNote')})</td>
              </tr>
              <tr className="border-b border-brand-100">
                <td className="py-2 font-medium text-brand-700">{t('guide.costs.energyFee')}</td>
                <td className="py-2 text-brand-600">{t('guide.costs.energyFeeDesc')}</td>
              </tr>
              <tr>
                <td className="py-2 font-medium text-brand-700">{t('guide.costs.billingPeriod')}</td>
                <td className="py-2 text-brand-600">{t('guide.costs.billingPeriodDesc')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-brand-600 text-sm mb-3">{t('guide.costs.spotInfo')}</p>
        <p className="text-brand-500 text-sm">{t('guide.costs.interval')}</p>
        <h4 className="font-semibold text-brand-700 mt-4 mb-2">{t('guide.costs.portalTitle')}</h4>
        <p className="text-brand-600 text-sm">{t('guide.costs.portalDesc')}</p>
      </Section>

      {/* 9. Lisäkäyttäjät */}
      <Section number="9" id="additional" title={t('guide.additional.title')}>
        <p className="text-brand-600">{t('guide.additional.desc')}</p>
      </Section>

      {/* 10. Ongelmatilanteet */}
      <Section number="10" id="troubleshoot" title={t('guide.troubleshoot.title')}>
        <ol className="list-decimal list-inside text-brand-700 space-y-1">
          <li>{t('guide.troubleshoot.step1')}</li>
          <li>{t('guide.troubleshoot.step2')}</li>
          <li>{t('guide.troubleshoot.step3')}</li>
          <li>{t('guide.troubleshoot.step4')}</li>
        </ol>
      </Section>

      {/* FAQ */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-brand-700">{t('guide.faq.title')}</h2>
        <Faq q={t('guide.faq.q2')} a={t('guide.faq.a2')} />
        <Faq q={t('guide.faq.q3')} a={t('guide.faq.a3')} />
        <Faq q={t('guide.faq.q4')} a={t('guide.faq.a4')} />
      </div>

      {/* Yhteystiedot */}
      <div className="bg-brand-50 rounded-xl p-6">
        <h2 className="text-lg font-bold text-brand-700 mb-3">{t('guide.contact.title')}</h2>
        <ul className="space-y-1 text-brand-600 text-sm">
          <li><strong>{t('guide.contact.billing')}:</strong> <a href="mailto:asiakaspalvelu@myyrmaenhuolto.fi" className="text-brand-500 hover:underline">asiakaspalvelu@myyrmaenhuolto.fi</a></li>
          <li><strong>{t('guide.contact.technical')}:</strong> <a href="mailto:asiakaspalvelu@myyrmaenhuolto.fi" className="text-brand-500 hover:underline">asiakaspalvelu@myyrmaenhuolto.fi</a></li>
          <li><strong>{t('guide.contact.emergencyLabel')}:</strong> 112</li>
        </ul>
        <p className="text-brand-400 text-xs mt-3">{t('guide.contact.terms')}</p>
      </div>

      {/* Back to login */}
      <p className="text-center">
        <a href="/login" className="text-brand-500 hover:underline text-sm">
          {t('guide.backToLogin')}
        </a>
      </p>
    </div>
  )
}

function Section({ number, id, title, children }: { number: string; id?: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="space-y-3 scroll-mt-4">
      <h2 className="text-xl font-bold text-brand-700">
        <span className="text-brand-400 mr-2">{number}.</span>{title}
      </h2>
      {children}
    </div>
  )
}

function ImgCard({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="text-center">
      <img src={src} alt={alt} className="rounded-lg shadow-sm border border-brand-100 w-full" />
      <p className="text-xs text-brand-400 mt-1">{alt}</p>
    </div>
  )
}

function LoomEmbed({ id, label }: { id: string; label: string }) {
  return (
    <div className="mt-4">
      <p className="text-sm text-brand-600 mb-2">{label}</p>
      <div className="max-w-xs">
        <div style={{ position: 'relative', paddingBottom: '217%', height: 0 }}>
          <iframe
            src={`https://www.loom.com/embed/${id}`}
            frameBorder="0"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />
        </div>
      </div>
    </div>
  )
}

function Faq({ q, a }: { q: string; a: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <p className="font-medium text-brand-700 mb-1">{q}</p>
      <p className="text-brand-600 text-sm">{a}</p>
    </div>
  )
}
